import { JAMIE, SARAH, type MockCustomer } from '../../data/mockData.js';

export interface LetterScope { sessionId: string; generation: number; workerId: string }
export interface LetterAddress { recipient: string; line1: string; line2: string; city: string; state: string; zip: string }
export type LetterKind = 'ready' | 'attention';
export interface LetterTask {
  id: string;
  customer: MockCustomer;
  horse: string;
  address: LetterAddress;
  addressVersion: number;
  revision: number;
  draft: string;
  status: 'assigned' | 'ready_for_shipping' | 'needs_attention';
}
export interface LetterSource { mode: 'preview' | 'verified'; scope: LetterScope; tasks: LetterTask[] }
export interface LetterRequest {
  requestId: string; taskId: string; customerId: string; repId: string;
  scope: LetterScope; kind: LetterKind; draft: string; address: LetterAddress;
  addressVersion: number; taskRevision: number; note: string;
}
export interface LetterReceipt {
  requestId: string; taskId: string; customerId: string; repId: string;
  scope: LetterScope; status: 'ready_for_shipping' | 'needs_attention';
  request: LetterRequest; recordedAt: number; preparedAt: number | null;
  preparedBy: string | null; mailedAt: null; mode: 'preview' | 'verified';
}
export type LetterResult = { outcome: 'confirmed'; receipt: LetterReceipt } | { outcome: 'failed'; message?: string } | { outcome: 'unknown'; message?: string };
export interface LetterQueue {
  scope: LetterScope; mode: 'preview' | 'verified'; tasks: LetterTask[];
  index: number; pending: LetterRequest | null; receipts: LetterReceipt[]; message: string;
}
export const letterScopeKey = (scope: LetterScope): string => JSON.stringify([scope.sessionId, scope.generation, scope.workerId]);
export function validLetterSource(source: LetterSource | null, expected: LetterScope | null): source is LetterSource {
  return Boolean(source && expected && expected.sessionId && expected.workerId && Number.isSafeInteger(expected.generation) && expected.generation > 0 &&
    letterScopeKey(source.scope) === letterScopeKey(expected) && new Set(source.tasks.map(task => task.id)).size === source.tasks.length &&
    source.tasks.every(task => task.id && task.customer.id && task.status === 'assigned' && Number.isSafeInteger(task.revision) && task.revision > 0));
}
export function createLetterQueue(source: LetterSource): LetterQueue {
  return { scope: { ...source.scope }, mode: source.mode, tasks: source.tasks.map(task => ({ ...task, customer: { ...task.customer }, address: { ...task.address } })), index: 0, pending: null, receipts: [], message: '' };
}
export const currentLetter = (queue: LetterQueue): LetterTask | null => queue.tasks[queue.index] ?? null;
export function letterGate(queue: LetterQueue): string {
  const task = currentLetter(queue);
  if (!task) return 'Today’s assigned letters are finished.';
  if (queue.pending) return 'Confirm the existing save before moving on.';
  if (task.status !== 'assigned') return 'This letter was already reviewed.';
  if (!task.draft.trim()) return 'Add the letter wording first.';
  if (['recipient', 'line1', 'city', 'state', 'zip'].some(key => !task.address[key as keyof LetterAddress].trim())) return 'A complete mailing address is needed.';
  return '';
}
export function editLetter(queue: LetterQueue, taskId: string, change: { draft?: string; address?: LetterAddress }): LetterQueue {
  const task = currentLetter(queue);
  if (!task || task.id !== taskId || queue.pending || task.status !== 'assigned') return queue;
  const addressChanged = change.address && JSON.stringify(change.address) !== JSON.stringify(task.address);
  const draftChanged = change.draft !== undefined && change.draft !== task.draft;
  if (!addressChanged && !draftChanged) return queue;
  const next = { ...task, draft: change.draft ?? task.draft, address: change.address ? { ...change.address } : task.address, addressVersion: task.addressVersion + (addressChanged ? 1 : 0), revision: task.revision + 1 };
  return { ...queue, tasks: queue.tasks.map(row => row.id === taskId ? next : row), message: '' };
}
export function beginLetter(queue: LetterQueue, kind: LetterKind, note = ''): { queue: LetterQueue; request: LetterRequest } {
  const task = currentLetter(queue);
  if (!task || queue.pending || task.status !== 'assigned') throw new Error('The current letter cannot be saved again.');
  if (kind === 'ready' && letterGate(queue)) throw new Error(letterGate(queue));
  if (kind === 'attention' && !note.trim()) throw new Error('Record what still needs attention.');
  const request: LetterRequest = {
    requestId: JSON.stringify([queue.scope.sessionId, queue.scope.generation, task.id, task.revision, kind]),
    taskId: task.id, customerId: task.customer.id, repId: queue.scope.workerId, scope: { ...queue.scope },
    kind, draft: task.draft, address: { ...task.address }, addressVersion: task.addressVersion, taskRevision: task.revision, note: note.trim(),
  };
  return { queue: { ...queue, pending: request, message: 'Saving readiness…' }, request };
}
export function previewLetterResult(request: LetterRequest, now: number): LetterResult {
  return { outcome: 'confirmed', receipt: { requestId: request.requestId, taskId: request.taskId, customerId: request.customerId,
    repId: request.repId, scope: { ...request.scope }, status: request.kind === 'ready' ? 'ready_for_shipping' : 'needs_attention',
    request: { ...request, scope: { ...request.scope }, address: { ...request.address } }, recordedAt: now,
    preparedAt: request.kind === 'ready' ? now : null, preparedBy: request.kind === 'ready' ? request.repId : null,
    mailedAt: null, mode: 'preview' } };
}
export function settleLetter(queue: LetterQueue, request: LetterRequest, result: LetterResult): LetterQueue {
  const task = currentLetter(queue);
  if (!task || !queue.pending || queue.pending.requestId !== request.requestId || JSON.stringify(queue.pending) !== JSON.stringify(request) || letterScopeKey(queue.scope) !== letterScopeKey(request.scope)) return queue;
  if (request.taskId !== task.id || request.customerId !== task.customer.id || request.taskRevision !== task.revision || request.addressVersion !== task.addressVersion || request.draft !== task.draft || JSON.stringify(request.address) !== JSON.stringify(task.address)) return { ...queue, message: 'The letter changed. The save is unconfirmed; stay with this task.' };
  if (result.outcome === 'unknown') return { ...queue, message: result.message || 'Readiness is unconfirmed. Check the existing save before moving on.' };
  if (result.outcome === 'failed') return { ...queue, pending: null, message: result.message || 'Readiness was not saved. This letter and its wording are retained; try again.' };
  const receipt = result.receipt;
  const status = request.kind === 'ready' ? 'ready_for_shipping' : 'needs_attention';
  if (receipt.mode !== queue.mode || receipt.requestId !== request.requestId || receipt.taskId !== task.id || receipt.customerId !== task.customer.id || receipt.repId !== queue.scope.workerId || letterScopeKey(receipt.scope) !== letterScopeKey(queue.scope) || JSON.stringify(receipt.request) !== JSON.stringify(request) || receipt.status !== status || receipt.mailedAt !== null || !Number.isFinite(receipt.recordedAt) || (request.kind === 'ready' ? receipt.preparedAt !== receipt.recordedAt || receipt.preparedBy !== request.repId : receipt.preparedAt !== null || receipt.preparedBy !== null)) return { ...queue, message: 'The readiness result does not match this letter. Check the existing save before moving on.' };
  if (queue.receipts.some(item => item.requestId === request.requestId || item.taskId === request.taskId)) return queue;
  return { ...queue, tasks: queue.tasks.map(row => row.id === task.id ? { ...row, status } : row), index: queue.index + 1, pending: null,
    receipts: [...queue.receipts, receipt], message: `${queue.mode === 'preview' ? 'Local preview: ' : ''}${status === 'ready_for_shipping' ? 'letter ready for Shipping' : 'attention note recorded'}. No mailing has been recorded.` };
}

// W37b (2026-10-20): one row of GET /v1/letters/queue. Shape READ from
// services/api-server/sinks/letters-queue-sink.mjs toRow(): the row is a
// readiness RECEIPT (status ready_for_shipping) from
// letters.letter_readiness, not an assigned drafting task, so it carries
// ids and timestamps but no customer name, address or wording.
// MEASURED 2026-10-20: `{result: "OK", items: []}` (Postgres empty).
export interface LetterQueueRow {
  taskId: string | null;
  repId: string | null;
  customerId: string | null;
  kind: string | null;
  addressVersion: number | null;
  note: string | null;
  preparedAt: number | null;
  preparedBy: string | null;
  recordedAt: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function optString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
function optNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Map the queue body defensively; rows that are not objects are dropped. */
export function mapLettersQueue(body: unknown): LetterQueueRow[] {
  const list = isRecord(body) && Array.isArray(body.items) ? body.items : [];
  const out: LetterQueueRow[] = [];
  for (const raw of list) {
    if (!isRecord(raw)) continue;
    out.push({
      taskId: optString(raw.taskId),
      repId: optString(raw.repId),
      customerId: optString(raw.customerId),
      kind: optString(raw.kind),
      addressVersion: optNumber(raw.addressVersion),
      note: optString(raw.note),
      preparedAt: optNumber(raw.preparedAt),
      preparedBy: optString(raw.preparedBy),
      recordedAt: optNumber(raw.recordedAt),
    });
  }
  return out;
}

// W47b (2026-10-21): the W47 api-server composes GET /v1/letters/queue as
// {result, items, mode, day, rep, readiness, mailed, selection} (READ
// services/api-server/sinks/letters-queue-source.mjs composeLettersQueueSink
// and routes/stubs-w3c.mjs createLettersQueueHandler). `items` keeps the
// readiness-receipt rows above; `selection` is the rep's end-of-day queue
// selected from HubSpot (one item per lapsed, owned contact with a complete
// mailing address), `mailed` says whether a mailing event source exists
// (MEASURED: it does not, reason NO_MAILING_EVENT_SOURCE). A body without a
// `selection` key is the pre-W47 shape and keeps the W37b rendering.
//
// Item shape (READ lettersQueueItem in letters-queue-source.mjs; the W47
// fixtures in sinks/test/w47-fixtures.mjs feed it):
//   contactId, name {first, last, full}, address {recipient, line1, line2,
//   city, state, zip, country}, addressChecklist {recipient, line1, city,
//   state, zip, complete}, lastOrder {name, date, daysSince, ordersCount,
//   lifetimeOrderCount, totalSpent, lifetimeValue, firstOrderDate, product},
//   subscription {rechargeStatus, status, productSku, lapsed}, horse {name},
//   flags, why, messagePack {key, doc, section, variant, label, note}.
// Every field is mapped defensively; a missing value stays null and is
// labelled honestly on screen, never defaulted into a fact.

export const ADDRESS_FIELD_LABELS: ReadonlyArray<{ key: 'recipient' | 'line1' | 'city' | 'state' | 'zip'; label: string }> = [
  { key: 'recipient', label: 'Recipient' },
  { key: 'line1', label: 'Street' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
];

export interface LetterSelectionItem {
  contactId: string;
  name: { first: string | null; last: string | null; full: string };
  initials: string;
  address: { recipient: string | null; line1: string | null; line2: string | null; city: string | null; state: string | null; zip: string | null; country: string | null };
  addressChecklist: { complete: boolean; missing: string[] };
  lastOrder: { name: string | null; date: string | null; daysSince: number | null; ordersCount: number | null; totalSpent: number | null; lifetimeValue: number | null; product: string | null };
  subscription: { lapsed: boolean; status: string | null };
  horse: string | null;
  why: string | null;
  messagePack: { key: string | null; doc: string | null; section: string | null; variant: string | null; label: string | null; note: string | null };
}

export interface LetterSelection {
  /** False when the api-server has no HubSpot read wired (reason names it). */
  available: boolean;
  /** 'READINESS_RECORDED_TODAY' when the rep already recorded readiness today. */
  suppressed: string | null;
  /** 'NO_LAPSED_CONTACTS_OWNED' | 'REP_HAS_NO_OWNER_RECORD' | 'HUBSPOT_PAT_NOT_WIRED' | 'VIEWER_REQUIRED' | null. */
  reason: string | null;
  note: string | null;
  /** HubSpot search total for the rule (MEASURED 0 for every owner until the re-import). */
  total: number | null;
  items: LetterSelectionItem[];
  rule: { lapseDays: number | null; cutoffDate: string | null; perRepPerDay: number | null } | null;
}

export interface LettersMailed {
  available: boolean;
  /** 'NO_MAILING_EVENT_SOURCE' on this deployment (MEASURED). */
  reason: string | null;
  note: string | null;
}

export interface LettersQueueBody {
  /** The readiness receipts (`items`), exactly as mapLettersQueue reads them. */
  receipts: LetterQueueRow[];
  /** null when the body carries no `selection` key (pre-W47 api-server). */
  selection: LetterSelection | null;
  mailed: LettersMailed | null;
  mode: string | null;
}

export const NO_MAILING_EVENT_SOURCE = 'NO_MAILING_EVENT_SOURCE';
export const NO_LAPSED_CONTACTS_OWNED = 'NO_LAPSED_CONTACTS_OWNED';
export const REP_HAS_NO_OWNER_RECORD = 'REP_HAS_NO_OWNER_RECORD';
export const READINESS_RECORDED_TODAY = 'READINESS_RECORDED_TODAY';

function optBool(value: unknown): boolean | null {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
}

export function letterInitials(first: string | null, last: string | null, fallback: string): string {
  const out = [first, last].map(part => (part ?? '').trim().charAt(0).toUpperCase()).join('');
  return out || fallback.slice(0, 2).toUpperCase() || '??';
}

/** True when the body is the W47 composed shape (carries `selection`). */
export function hasLettersSelection(body: unknown): boolean {
  return isRecord(body) && isRecord(body.selection);
}

export function mapLetterSelectionItem(raw: unknown): LetterSelectionItem | null {
  if (!isRecord(raw)) return null;
  const contactId = optString(raw.contactId) ?? optString(raw.id);
  if (!contactId) return null;
  const nameRaw = raw.name;
  const first = isRecord(nameRaw) ? optString(nameRaw.first) : null;
  const last = isRecord(nameRaw) ? optString(nameRaw.last) : null;
  const full = isRecord(nameRaw) ? optString(nameRaw.full) : optString(nameRaw);
  const addr = isRecord(raw.address) ? raw.address : {};
  const address = {
    recipient: optString(addr.recipient) ?? full ?? null,
    line1: optString(addr.line1),
    line2: optString(addr.line2),
    city: optString(addr.city),
    state: optString(addr.state),
    zip: optString(addr.zip),
    country: optString(addr.country),
  };
  const checklistRaw = isRecord(raw.addressChecklist) ? raw.addressChecklist : {};
  const present: Record<'recipient' | 'line1' | 'city' | 'state' | 'zip', boolean> = {
    recipient: optBool(checklistRaw.recipient) ?? address.recipient !== null,
    line1: optBool(checklistRaw.line1) ?? address.line1 !== null,
    city: optBool(checklistRaw.city) ?? address.city !== null,
    state: optBool(checklistRaw.state) ?? address.state !== null,
    zip: optBool(checklistRaw.zip) ?? address.zip !== null,
  };
  const listedMissing = Array.isArray(checklistRaw.missing) ? checklistRaw.missing.filter((m): m is string => typeof m === 'string') : null;
  const missing = listedMissing ?? ADDRESS_FIELD_LABELS.filter(f => !present[f.key]).map(f => f.label);
  const complete = optBool(checklistRaw.complete) ?? missing.length === 0;
  const order = isRecord(raw.lastOrder) ? raw.lastOrder : {};
  const sub = isRecord(raw.subscription) ? raw.subscription : {};
  const horseRaw = isRecord(raw.horse) ? raw.horse.name : raw.horse;
  const pack = isRecord(raw.messagePack) ? raw.messagePack : {};
  const fullName = full ?? [first, last].filter(Boolean).join(' ');
  return {
    contactId,
    name: { first, last, full: fullName || `HubSpot contact ${contactId}` },
    initials: letterInitials(first, last, fullName || contactId),
    address,
    addressChecklist: { complete, missing },
    lastOrder: {
      name: optString(order.name),
      date: optString(order.date),
      daysSince: optNumber(order.daysSince),
      ordersCount: optNumber(order.ordersCount) ?? optNumber(order.count),
      totalSpent: optNumber(order.totalSpent),
      lifetimeValue: optNumber(order.lifetimeValue),
      product: optString(order.product),
    },
    subscription: { lapsed: optBool(sub.lapsed) === true, status: optString(sub.status) ?? optString(sub.rechargeStatus) },
    horse: optString(horseRaw),
    why: optString(raw.why),
    messagePack: {
      key: optString(pack.key),
      doc: optString(pack.doc),
      section: optString(pack.section),
      variant: optString(pack.variant),
      label: optString(pack.label),
      note: optString(pack.note),
    },
  };
}

/** Map the whole W47 body; the pre-W47 shape yields `selection: null`. */
export function mapLettersQueueBody(body: unknown): LettersQueueBody {
  const receipts = mapLettersQueue(body);
  if (!isRecord(body)) return { receipts, selection: null, mailed: null, mode: null };
  const sel = isRecord(body.selection) ? body.selection : null;
  const rule = sel && isRecord(sel.rule) ? sel.rule : null;
  const selection: LetterSelection | null = sel
    ? {
        available: optBool(sel.available) ?? true,
        suppressed: optString(sel.suppressed),
        reason: optString(sel.reason),
        note: optString(sel.note),
        total: optNumber(sel.total),
        items: (Array.isArray(sel.items) ? sel.items : []).map(mapLetterSelectionItem).filter((item): item is LetterSelectionItem => item !== null),
        rule: rule ? { lapseDays: optNumber(rule.lapseDays), cutoffDate: optString(rule.cutoffDate), perRepPerDay: optNumber(rule.perRepPerDay) } : null,
      }
    : null;
  const mailedRaw = isRecord(body.mailed) ? body.mailed : null;
  const mailed: LettersMailed | null = mailedRaw
    ? { available: optBool(mailedRaw.available) ?? false, reason: optString(mailedRaw.reason), note: optString(mailedRaw.note) }
    : null;
  return { receipts, selection, mailed, mode: optString(body.mode) };
}

export function previewLetterSource(): LetterSource {
  const people = [
    [SARAH.id, 'Sarah', 'Mitchell', 'Duke'], ['letter_demo_taylor', 'Taylor', 'Brooks', 'Scout'],
    ['letter_demo_jordan', 'Jordan', 'Hayes', 'Bella'], ['letter_demo_emma', 'Emma', 'Collins', 'Jasper'],
    ['letter_demo_alex', 'Alex', 'Parker', 'Willow'], ['letter_demo_morgan', 'Morgan', 'Reed', 'Charlie'],
    ['letter_demo_casey', 'Casey', 'Bennett', 'Ruby'], ['letter_demo_riley', 'Riley', 'Adams', 'Cooper'],
    ['letter_demo_avery', 'Avery', 'Wilson', 'Luna'], ['letter_demo_sam', 'Sam', 'Foster', 'Ace'],
  ];
  return { mode: 'preview', scope: { sessionId: 'approved-letters-review', generation: 1, workerId: JAMIE.id }, tasks: people.map(([id, first, last, horse], index) => ({
    id: `LETTER-DEMO-${index + 1}`, customer: index === 0 ? SARAH : {
      id, firstName: first, fullName: `${first} ${last}`, initials: first[0] + last[0], customerSince: 'Assigned example',
      city: 'Nashville', stateCode: 'TN', localTime: 'Central time', phone: `(615) 555-01${String(48 + index).padStart(2, '0')}`,
      email: `${first}.${last}@example.com`.toLowerCase(), preferredChannel: 'Text message',
    }, horse, address: { recipient: `${first} ${last}`, line1: index ? `${100 + index} Example Lane` : '8124 Meadow Lane', line2: index ? '' : 'Barn entrance', city: 'Nashville', state: 'TN', zip: '37221' },
    addressVersion: 1, revision: 1, status: 'assigned',
    draft: `Hi ${first},\n\nI wanted to see how you and ${horse} are doing. If you have questions about ${horse}’s routine or need help, I’m here for you.\n\nJamie Dorsey\n100X Equine · (615) 555-0100`,
  })) };
}
